import { Target } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";

const RATING_COLORS = {
  "A+": "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  A: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300",
  "B+": "border-sky-400/30 bg-sky-400/5 text-sky-300",
  B: "border-sky-400/20 bg-sky-400/5 text-sky-300",
  "C+": "border-zinc-400/30 bg-zinc-400/5 text-zinc-300",
  C: "border-zinc-400/20 bg-zinc-400/5 text-zinc-400",
  PASS: "border-white/10 bg-white/5 text-zinc-500",
};

// NRFI/YRFI (No Run / Yes Run First Inning) -- a FOURTH, fully separate model from Full Game,
// F5 and Moneyline. Uses the exact same nrfi_yrfi values already returned by the prediction
// endpoint (no new calculation). Team-only baseline plus each starter's OWN real first-inning
// runs-allowed history -- never general ERA, never bullpen, never a market-derived probability.
export const NRFIYRFIHeroCard = ({ nrfiYrfi, testId }) => {
  if (!nrfiYrfi) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <EmptyState icon={Target} title="First Inning O/U Unavailable" description="Loading prediction data." testId={`${testId}-empty`} />
      </div>
    );
  }

  const ok = nrfiYrfi.status === "ok";
  const sp = nrfiYrfi.components?.starting_pitcher;
  const edge = nrfiYrfi.edge;

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      {ok ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">NRFI</span>
              <p data-testid={`${testId}-nrfi-probability`} className="mt-1 font-heading text-2xl text-white">
                {(nrfiYrfi.nrfi_probability * 100).toFixed(1)}%
              </p>
              {edge && <p className="font-mono text-[10px] text-zinc-500">edge {(edge.nrfi_edge_percentage * 100).toFixed(1)}%</p>}
            </div>
            <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">YRFI</span>
              <p data-testid={`${testId}-yrfi-probability`} className="mt-1 font-heading text-2xl text-white">
                {(nrfiYrfi.yrfi_probability * 100).toFixed(1)}%
              </p>
              {edge && <p className="font-mono text-[10px] text-zinc-500">edge {(edge.yrfi_edge_percentage * 100).toFixed(1)}%</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Exp. Away 1st Runs</span>
              <p data-testid={`${testId}-expected-away-runs`} className="mt-1 font-mono text-sm text-zinc-200">
                {nrfiYrfi.expected_away_1st_runs}
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-eb-bg p-3 text-center">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Exp. Home 1st Runs</span>
              <p data-testid={`${testId}-expected-home-runs`} className="mt-1 font-mono text-sm text-zinc-200">
                {nrfiYrfi.expected_home_1st_runs}
              </p>
            </div>
          </div>

          {sp && (
            <p data-testid={`${testId}-starter-note`} className="font-mono text-[11px] text-zinc-500">
              Starter first-inning history: away starter {sp.away_applied ? `${sp.away_starts} starts, ${sp.away_avg_runs_allowed_1st} runs allowed/start` : "Insufficient Data"} &middot;{" "}
              home starter {sp.home_applied ? `${sp.home_starts} starts, ${sp.home_avg_runs_allowed_1st} runs allowed/start` : "Insufficient Data"}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-eb-bg p-3">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Sportsbook Market (Display Only)</span>
              {nrfiYrfi.market_line ? (
                <div data-testid={`${testId}-market-line`} className="mt-1 flex flex-wrap items-baseline gap-3 font-mono text-xs text-zinc-300">
                  <span>{nrfiYrfi.market_line.sportsbook} O/U {nrfiYrfi.market_line.line_value}</span>
                  <span>NRFI (Under): {nrfiYrfi.market_line.nrfi_price > 0 ? `+${nrfiYrfi.market_line.nrfi_price}` : nrfiYrfi.market_line.nrfi_price}</span>
                  <span>YRFI (Over): {nrfiYrfi.market_line.yrfi_price > 0 ? `+${nrfiYrfi.market_line.yrfi_price}` : nrfiYrfi.market_line.yrfi_price}</span>
                </div>
              ) : (
                <p data-testid={`${testId}-market-line-unavailable`} className="mt-1 font-mono text-xs text-zinc-500">
                  Unavailable
                </p>
              )}
            </div>
            {edge && (
              <div className={`shrink-0 rounded-md border px-3 py-1.5 text-center font-mono text-xs uppercase tracking-wide ${RATING_COLORS[edge.bet_rating] || RATING_COLORS.PASS}`} data-testid={`${testId}-bet-rating`}>
                <div className="text-[10px] text-zinc-500">{edge.recommended_side ? `Lean ${edge.recommended_side}` : "No Edge"}</div>
                <div className="text-base font-heading">{edge.bet_rating}</div>
              </div>
            )}
          </div>

          <p data-testid={`${testId}-disclaimer`} className="font-mono text-[10px] text-zinc-500">
            Team + starter first-inning history only. No bullpen. Market shown for reference only -- edge/bet rating compare
            the model's own probability to the no-vig fair price, never feeding back into the model itself.
          </p>
        </>
      ) : (
        <EmptyState icon={Target} title="Insufficient Data For NRFI/YRFI" description={nrfiYrfi.reason || "Not enough first-inning data yet."} testId={`${testId}-insufficient`} />
      )}
    </div>
  );
};
